import React, { useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { useTable, useSortBy, useFilters, useGlobalFilter } from 'react-table';

// Register Chart.js components
Chart.register(...registerables);

// Define a default UI for filtering
function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length;

  return (
    <input
      value={filterValue || ''}
      onChange={e => {
        setFilter(e.target.value || undefined);
      }}
      placeholder={`Search ${count} records...`}
      style={{ width: '100%', padding: '5px', marginTop: '5px' }}
    />
  );
}

// Create an interactive table component
function ReactTable({ columns, data }) {
  const defaultColumn = useMemo(
    () => ({
      // Default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFilters,
    useGlobalFilter,
    useSortBy
  );

  return (
    <table {...getTableProps()} style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        {headerGroups.map(headerGroup => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map(column => (
              <th 
                {...column.getHeaderProps(column.getSortByToggleProps())}
                style={{ 
                  padding: '10px', 
                  borderBottom: '2px solid #ddd',
                  background: '#f2f2f2',
                  cursor: 'pointer'
                }}
              >
                {column.render('Header')}
                <span>
                  {column.isSorted
                    ? column.isSortedDesc
                      ? ' 🔽'
                      : ' 🔼'
                    : ''}
                </span>
                <div>{column.canFilter ? column.render('Filter') : null}</div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map(cell => {
                // Check if the cell value is a number and if the column ID includes 'Profit'
                const isProfitCell = cell.column.id.includes('Profit') && typeof cell.value === 'number';
                const isPositive = isProfitCell && cell.value >= 0;
                
                return (
                  <td 
                    {...cell.getCellProps()}
                    style={{ 
                      padding: '10px', 
                      borderBottom: '1px solid #ddd',
                      color: isProfitCell ? (isPositive ? 'green' : 'red') : 'inherit'
                    }}
                  >
                    {cell.render('Cell')}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ResultsSection({ data }) {
  const { grouped_trade, monthly_profit, ticker_profit, adjustment_errors } = data;

  // Format data for display
  const formatValue = (value, type = '') => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: type === 'currency' ? 'currency' : 'decimal',
        currency: 'INR'
      });
    }
    return value;
  };

  const plColumns = ['RealizedPL', 'UnrealizedPL', 'NetPL'];

  // Define columns for each table
  const groupedTradeColumns = useMemo(
    () => 
      grouped_trade && grouped_trade.length > 0 
        ? Object.keys(grouped_trade[0]).map(key => ({
            Header: key,
            accessor: key,
            Cell: ({ value }) => formatValue(value)
          }))
        : [],
    [grouped_trade]
  );

  const monthlyProfitColumns = useMemo(
    () => 
      monthly_profit && monthly_profit.length > 0 
        ? Object.keys(monthly_profit[0]).map(key => ({
            Header: key,
            accessor: key,
            Cell: ({ value }) => formatValue(value)
          }))
        : [],
    [monthly_profit]
  );

  const tickerProfitColumns = useMemo(
    () => {
      if (!ticker_profit || ticker_profit.length === 0) return [];
      
      return Object.keys(ticker_profit[0]).map(key => ({
        Header: key === 'RealizedPL' ? 'Realized P&L' :
                key === 'UnrealizedPL' ? 'Unrealized P&L' :
                key === 'NetPL' ? 'Net P&L' : key,
        accessor: key,
        Cell: ({ value }) => formatValue(value, plColumns.includes(key) ? 'currency' : ''),
        sortType: (a, b) => {
          if (plColumns.includes(key)) {
            return a.values[key] - b.values[key];
          }
          return a.values[key] > b.values[key] ? 1 : -1;
        }
      }));
    },
    [ticker_profit]
  );

  const adjustmentErrorsColumns = useMemo(
    () => 
      adjustment_errors && adjustment_errors.length > 0 
        ? Object.keys(adjustment_errors[0]).map(key => ({
            Header: key,
            accessor: key,
            Cell: ({ value }) => formatValue(value)
          }))
        : [],
    [adjustment_errors]
  );

  // Monthly profit chart configuration
  const monthlyChartData = {
    labels: monthly_profit.map(item => item.Month),
    datasets: [{
      label: 'Monthly P&L',
      data: monthly_profit.map(item => (item.RealizedPL || 0) + (item.UnrealizedPL || 0)),
      backgroundColor: monthly_profit.map(item => 
        ((item.RealizedPL || 0) + (item.UnrealizedPL || 0)) >= 0 
        ? 'rgba(75, 192, 192, 0.6)' 
        : 'rgba(255, 99, 132, 0.6)'
      ),
      borderColor: monthly_profit.map(item => 
        ((item.RealizedPL || 0) + (item.UnrealizedPL || 0)) >= 0 
        ? 'rgba(75, 192, 192, 1)' 
        : 'rgba(255, 99, 132, 1)'
      ),
      borderWidth: 1
    }]
  };

  // Ticker chart configuration
  const tickerChartData = {
    labels: ticker_profit.map(item => item.Ticker),
    datasets: [{
      label: 'P&L',
      data: ticker_profit.map(item => (item.RealizedPL || 0) + (item.UnrealizedPL || 0)),
      backgroundColor: ticker_profit.map(item => 
        ((item.RealizedPL || 0) + (item.UnrealizedPL || 0)) >= 0 
        ? 'rgba(75, 192, 192, 0.6)' 
        : 'rgba(255, 99, 132, 0.6)'
      ),
      borderColor: ticker_profit.map(item => 
        ((item.RealizedPL || 0) + (item.UnrealizedPL || 0)) >= 0 
        ? 'rgba(75, 192, 192, 1)' 
        : 'rgba(255, 99, 132, 1)'
      ),
      borderWidth: 1
    }]
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `P&L: ${new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR'
            }).format(context.parsed.y)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'P&L'
        }
      }
    }
  };

  return (
    <div id="results">
      <h2>Monthly Profitability</h2>
      <div className="chart-container" style={{ height: '300px', marginBottom: '30px' }}>
        <Bar data={monthlyChartData} options={chartOptions} />
      </div>

      <h2>Profit by Ticker</h2>
      <div className="chart-container" style={{ height: '300px', marginBottom: '30px' }}>
        <Bar data={tickerChartData} options={chartOptions} />
      </div>

      <h2>Detailed Trade Analysis</h2>
      <div id="trades-table" style={{ marginBottom: '30px', overflowX: 'auto' }}>
        {grouped_trade && grouped_trade.length > 0 ? (
          <ReactTable columns={groupedTradeColumns} data={grouped_trade} />
        ) : (
          <p>No data available</p>
        )}
      </div>

      <h2>Monthly Profitability Details</h2>
      <div id="monthly-table" style={{ marginBottom: '30px', overflowX: 'auto' }}>
        {monthly_profit && monthly_profit.length > 0 ? (
          <ReactTable columns={monthlyProfitColumns} data={monthly_profit} />
        ) : (
          <p>No data available</p>
        )}
      </div>

      <h2>Profit by Ticker Details</h2>
      <div id="ticker-table" style={{ marginBottom: '30px', overflowX: 'auto' }}>
        {ticker_profit && ticker_profit.length > 0 ? (
          <ReactTable columns={tickerProfitColumns} data={ticker_profit} />
        ) : (
          <p>No data available</p>
        )}
      </div>

      <h2>Trades with Adjustment Issues</h2>
      <div id="adjustment-errors-table" style={{ marginBottom: '30px', overflowX: 'auto' }}>
        {adjustment_errors && adjustment_errors.length > 0 ? (
          <ReactTable columns={adjustmentErrorsColumns} data={adjustment_errors} />
        ) : (
          <p>No adjustment errors found.</p>
        )}
      </div>
    </div>
  );
}

export default ResultsSection;
